import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useState } from 'react'
import {
  addServer,
  loadLastUsername,
  loadSelectedServerId,
  loadServers,
  type MantaServer,
  removeServer,
  saveLastUsername,
  saveSelectedServerId,
} from './serverStore.js'

const ADD_VALUE = '__add__'

export type Credentials = {
  username: string
  password: string
}

const AddServerDialog = ({
  open,
  onClose,
  onAdd,
}: {
  open: boolean
  onClose: () => void
  onAdd: (server: MantaServer) => void
}) => {
  const [name, setName] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState('')

  const reset = () => {
    setName('')
    setHost('')
    setPort('')
  }

  const canSave = host.trim() !== ''

  const handleAdd = () => {
    const trimmedPort = port.trim()
    const entry = addServer({
      name:
        name.trim() ||
        (trimmedPort ? `${host.trim()}:${trimmedPort}` : host.trim()),
      host: host.trim(),
      port: trimmedPort,
    })
    onAdd(entry)
    reset()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Manta Server hinzufügen</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Host"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            fullWidth
          />
          <TextField
            label="Port (optional)"
            helperText="Leer lassen für 443/wss bzw. 80/ws"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset()
            onClose()
          }}
        >
          Abbrechen
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={handleAdd}>
          Hinzufügen
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const LoginScreen = ({
  onConnect,
  error,
}: {
  onConnect: (server: MantaServer, credentials: Credentials) => void
  error?: string
}) => {
  const [servers, setServers] = useState<MantaServer[]>(loadServers)
  const [selectedId, setSelectedId] = useState<string>(
    () => loadSelectedServerId() || loadServers()[0]?.id || '',
  )
  const [username, setUsername] = useState(loadLastUsername)
  const [password, setPassword] = useState('')
  const [addOpen, setAddOpen] = useState(false)

  const selectedServer = servers.find((s) => s.id === selectedId)
  const canConnect = !!selectedServer && username.trim() !== ''

  const handleServerChange = (value: string) => {
    if (value === ADD_VALUE) {
      setAddOpen(true)
      return
    }
    setSelectedId(value)
  }

  const handleAdded = (server: MantaServer) => {
    setServers(loadServers())
    setSelectedId(server.id)
    setAddOpen(false)
  }

  const handleDelete = () => {
    if (!selectedServer) return
    const remaining = removeServer(selectedServer.id)
    setServers(remaining)
    setSelectedId(remaining[0]?.id || '')
  }

  const handleConnect = () => {
    if (!selectedServer) return
    saveSelectedServerId(selectedServer.id)
    saveLastUsername(username.trim())
    onConnect(selectedServer, { username: username.trim(), password })
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      p={2}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5">Anmeldung</Typography>
            <Typography variant="body2" color="text.secondary">
              Mit dem Manta Server verbinden
            </Typography>
          </Box>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          <TextField
            label="Benutzername"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Passwort"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canConnect) handleConnect()
            }}
            fullWidth
          />

          <Button
            variant="contained"
            size="large"
            disabled={!canConnect}
            onClick={handleConnect}
          >
            Verbinden
          </Button>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth size="small">
              <InputLabel id="manta-server-label">Manta Server</InputLabel>
              <Select
                labelId="manta-server-label"
                label="Manta Server"
                value={selectedServer ? selectedId : ''}
                onChange={(e) => handleServerChange(e.target.value)}
              >
                {servers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <ListItemText
                      primary={s.name}
                      secondary={s.port ? `${s.host}:${s.port}` : s.host}
                    />
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={ADD_VALUE}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Hinzufügen
                </MenuItem>
              </Select>
            </FormControl>
            {selectedServer && (
              <Tooltip title="Server entfernen">
                <IconButton
                  onClick={handleDelete}
                  aria-label="Server entfernen"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>

      <AddServerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdded}
      />
    </Box>
  )
}
